/**
 * Supabase Connection Test Utility
 * Run this to verify database schema and RLS policies are working
 */

import { supabase } from '../lib/api/supabase';

export const testSupabaseConnection = async () => {
  const results = {
    connection: false,
    authentication: false,
    tables: {},
    rlsPolicies: {},
    errors: []
  };

  try {
    // 1. Test basic connection
    console.log('🔍 Testing Supabase connection...');
    const { data: healthCheck, error: healthError } = await supabase
      .from('profiles')
      .select('count')
      .limit(1);
    
    if (healthError) {
      results.errors.push(`Connection error: ${healthError.message}`);
      return results;
    }
    
    results.connection = true;
    console.log('✅ Connection successful');

    // 2. Test authentication
    console.log('🔍 Testing authentication...');
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError) {
      results.errors.push(`Auth error: ${authError.message}`);
    } else if (user) {
      results.authentication = true;
      console.log(`✅ Authenticated as: ${user.email}`);
      
      // Get user role
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (profile) {
        console.log(`👤 User role: ${profile.role}`);
      }
    } else {
      console.log('⚠️  Not authenticated (this is OK for testing read access)');
    }

    // 3. Test each table
    const tables = [
      'customers',
      'products',
      'orders',
      'bottles',
      'oils',
      'shipping',
      'returned_pool',
      'ads_ledger',
      'app_settings',
      'profiles'
    ];

    console.log('\n🔍 Testing table access...');
    
    for (const table of tables) {
      try {
        // Test read access
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .limit(1);
        
        if (error) {
          results.tables[table] = {
            read: false,
            error: error.message
          };
          console.log(`❌ ${table}: Read failed - ${error.message}`);
        } else {
          results.tables[table] = {
            read: true,
            count: data?.length || 0
          };
          console.log(`✅ ${table}: Read successful (${data?.length || 0} rows)`);
        }
      } catch (err) {
        results.tables[table] = {
          read: false,
          error: err.message
        };
        console.log(`❌ ${table}: ${err.message}`);
      }
    }

    // 4. Test write access (only if authenticated)
    if (user) {
      console.log('\n🔍 Testing write access...');
      
      // Test customer insert
      const testCustomer = {
        id: `test_${Date.now()}`,
        name: 'Test Customer',
        mobile: '1234567890',
        email: 'test@example.com',
        state: 'Test State',
        city: 'Test City',
        orders: 0,
        completed: 0,
        cancels: [],
        status: 'active'
      };

      const { data: insertData, error: insertError } = await supabase
        .from('customers')
        .insert([testCustomer])
        .select();

      if (insertError) {
        results.rlsPolicies.insert = false;
        console.log(`❌ Insert test failed: ${insertError.message}`);
        
        if (insertError.message.includes('policy')) {
          console.log('⚠️  This likely means you need admin role. Check your profile role in Supabase.');
        }
      } else {
        results.rlsPolicies.insert = true;
        console.log('✅ Insert test successful');

        // Clean up test data
        await supabase
          .from('customers')
          .delete()
          .eq('id', testCustomer.id);
        
        console.log('🧹 Test data cleaned up');
      }
    }

    // 5. Summary
    console.log('\n📊 Test Summary:');
    console.log(`Connection: ${results.connection ? '✅' : '❌'}`);
    console.log(`Authentication: ${results.authentication ? '✅' : '⚠️  Not logged in'}`);
    
    const successfulTables = Object.values(results.tables).filter(t => t.read).length;
    console.log(`Tables accessible: ${successfulTables}/${tables.length}`);
    
    if (results.errors.length > 0) {
      console.log('\n❌ Errors:');
      results.errors.forEach(err => console.log(`  - ${err}`));
    }

    return results;
  } catch (error) {
    console.error('❌ Test failed:', error);
    results.errors.push(error.message);
    return results;
  }
};

/**
 * Quick test function to call from browser console
 */
export const quickTest = async () => {
  console.log('='.repeat(60));
  console.log('SUPABASE CONNECTION TEST');
  console.log('='.repeat(60));
  
  const results = await testSupabaseConnection();
  
  console.log('\n' + '='.repeat(60));
  console.log('TEST COMPLETE');
  console.log('='.repeat(60));
  
  return results;
};

// Export for use in browser console
if (typeof window !== 'undefined') {
  window.testSupabase = quickTest;
}

